//
//  groupsPersonnel.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines the guides of the group and personnel pages.
//

import { Permission } from "src/api/Tmd.dtos";

import {
    helpTourTargets,
    hubspotTourTargets,
    navigationTourTargets,
    preferenceTourTargets
} from "../helpTourTargets";
import { notesGuides } from "./commonGuides";
import {
    actionsStop,
    cards,
    column,
    covering,
    createGuide,
    field,
    filterStop,
    grid,
    item,
    menu,
    saveStop,
    stop,
    type PageGuide,
    type PageGuideStop
} from "./pageGuide";
import {
    roleCreateGuide,
    roleEditGuide,
    roleListGuide,
    type RoleOwner
} from "./personnelRoleGuides";

/** An address block whose other lines the help lists; see institutions.ts. */
const address = (first: string, label: string, text: string, names: string[]): PageGuideStop =>
    covering(
        field(first, label, text),
        ...names.map(
            (name, index) =>
                [
                    name,
                    [
                        "Address Line 2",
                        "Suburb/City",
                        "ZIP/Postal Code",
                        "State/Province/Region",
                        "State Other, enabled when the state is Other",
                        "Country"
                    ][index]
                ] as [string, string]
        )
    );

const group: PageGuide = {
    intro: "A cooperative group's details: its identity, addresses and contacts, and the legal and notice details used on agreements. Its trials, personnel, notes and configuration are on the side menu.",
    stops: [
        field("name", "Name", "The group's usual name, shown throughout the system and in emails."),
        field(
            "otherNames",
            "Former Names",
            "Earlier names, so the group can still be found when searching for them."
        ),
        covering(
            field(
                "groupTypeId",
                "Group Type",
                "The kind of organisation. Choose Other and describe it in Group Type Other when none of the types fits."
            ),
            ["groupTypeOther", "Group Type Other, enabled when the type is Other"]
        ),
        field(
            "code",
            "Code",
            "The short code identifying the group in lists and reports. It is set when the group is created and cannot be changed afterwards."
        ),
        address(
            "address",
            "Street address",
            "The group's street address. Choose the country first so the right states are offered.",
            ["address2", "suburb", "postcode", "stateId", "stateOther", "countryId"]
        ),
        address(
            "mailAddress",
            "Postal address",
            "Where post should be sent, if different from the street address.",
            [
                "mailAddress2",
                "mailSuburb",
                "mailPostcode",
                "mailStateId",
                "mailStateOther",
                "mailCountryId"
            ]
        ),
        covering(
            field(
                "phone",
                "Phone",
                "The group's main phone number, used when contacting the group rather than a person."
            ),
            ["fax", "Fax"]
        ),
        field(
            "website",
            "Website",
            "The group's website address, including https://, so it opens from the group record."
        ),
        field(
            "legalName",
            "Group Legal Name",
            "The legal entity named on agreements, which can differ from the group's usual name."
        ),
        field(
            "agreementAddressOptionId",
            "Address",
            "Which address agreements use: the street address, the postal address, or a separate contracting party address, which appears when chosen."
        ),
        address(
            "contractingPartyAddress",
            "Contracting party address",
            "The address of the legal entity, used on agreements when the address option is the contracting party.",
            [
                "contractingPartyAddress2",
                "contractingPartySuburb",
                "contractingPartyPostcode",
                "contractingPartyStateId",
                "contractingPartyStateOther",
                "contractingPartyCountryId"
            ]
        ),
        field(
            "abn",
            "ABN (if applicable)",
            "The Australian Business Number of the legal entity, used on agreements and invoices."
        ),
        field(
            "acn",
            "ACN (if applicable)",
            "The Australian Company Number, if the legal entity has one."
        ),
        covering(
            field(
                "contactNotice",
                "Contact for Notices",
                "Who formal notices under an agreement, such as a breach or termination notice, are addressed to. Their email, phone and fax follow."
            ),
            ["emailNotice", "Email for Notices"],
            ["phoneNotice", "Phone for Notices"],
            ["faxNotice", "Fax for Notices"]
        ),
        field(
            "acceptElectronicSignature",
            "Does this group accept electronic signatures",
            "Whether the group accepts electronically signed agreements, or needs wet-ink signatures."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the group from lists and selections. Its history and trials are kept."
        ),
        field(
            "isDefault",
            "Is Default",
            "Marks the default group. The groups list shows which group is the default."
        ),
        saveStop("Submit saves the group's details. Submit And Return also goes back to the groups list.", Permission.GroupUpdate)
    ],
    rules: [
        "Name, Code and Group Type are required; Group Type Other is required when the type is Other.",
        "The street address needs Address Line 1, Suburb/City, State/Province/Region (or State Other), ZIP/Postal Code and Country."
    ]
};

const personnel: PageGuide = {
    intro: "A person's details: their name and profile, TROG membership, sign-in username, and one or more sets of contact details (one per workplace). Their roles, notes, files, login, OpenClinica access, HubSpot contact and email preferences are on the side menu.",
    stops: [
        field(
            "salutationId",
            "Salutation",
            "How the person is addressed in emails and letters, such as Dr or Prof."
        ),
        field("firstName", "First name", "The person's given name, as used in emails and lists."),
        field("lastName", "Last name", "The person's family name, as used in emails and lists."),
        field(
            "genderId",
            "Gender",
            "The person's gender, from the options maintained in settings. It is optional."
        ),
        field(
            "residentialPostcode",
            "Residential Postcode",
            "The postcode where the person lives, separate from their workplace addresses."
        ),
        covering(
            field(
                "personnelProfessionalDetails",
                "Contact Details",
                "A person can have a set of contact details for each workplace. Add, below the list, adds another set; each set has the fields below."
            ),
            ["personnelProfessionalDetails", "Contact Details"]
        ),
        field(
            "primaryEmail",
            "Primary Email",
            "The main email for this workplace. Sign-in accounts and HubSpot contacts are matched on the person's primary email, so keep it current."
        ),
        field(
            "secondaryEmail",
            "Secondary Email",
            "Another email address for this workplace, such as a shared department inbox."
        ),
        covering(field("phone", "Phone", "A work phone number for this workplace; Mobile follows for a mobile number."), [
            "mobile",
            "Mobile"
        ]),
        field(
            "primaryDetails",
            "Primary Details",
            "Tick on the set of contact details that are the person's main ones, used when a single email or phone is needed."
        ),
        covering(
            field(
                "institutionId",
                "Institution",
                "The institution this set of contact details is for. Choose Other and type its name in Institution Other when it is not in the list."
            ),
            ["institutionOther", "Institution Other, enabled when the institution is Other"]
        ),
        field(
            "department",
            "Department",
            "The department the person works in at this institution, as free text."
        ),
        field("jobTitle", "Job Title", "The person's position at this institution."),
        address(
            "addressLine1",
            "Address",
            "The workplace's address; choose the country first so the right states are offered.",
            [
                "addressLine2",
                "citySuburb",
                "postCode",
                "stateProvinceRegionId",
                "stateOther",
                "countryId"
            ]
        ),
        field(
            "cvDate",
            "CV Date",
            "The date of the latest CV on file. Upload the CV itself under Files."
        ),
        covering(
            field(
                "professionalDisciplineId",
                "Professional Discipline",
                "The person's profession, such as radiation oncologist or radiation therapist. Choose Other and describe it in Other Disciplines when it is not listed."
            ),
            ["otherDisciplines", "Other Disciplines, enabled when the discipline is Other"]
        ),
        covering(
            field(
                "personnelInterests",
                "Specialities/interests",
                "The person's clinical or research interests, used to find people for trials and working parties. Other Interests records any not listed."
            ),
            ["otherInterests", "Other Interests"]
        ),
        field(
            "membershipTypeId",
            "TROG membership type",
            "The person's TROG membership, if any. Membership is kept in step with HubSpot."
        ),
        field(
            "membershipExpiryDate",
            "TROG Membership Expiry Date",
            "When the person's TROG membership lapses."
        ),
        field(
            "userName",
            "Username",
            "The name the person signs in with. Their account itself is created from Login & Security."
        ),
        field(
            "comments",
            "Comments",
            "Anything else worth knowing about the person that does not fit a note."
        ),
        field(
            "active",
            "Active",
            "Clear when the person no longer works with TROG. They are hidden from lists and selections; their roles and history are kept."
        ),
        saveStop("Submit saves the person's details, including every set of contact details.", Permission.PersonnelUpdate)
    ]
};

const personnelDetails: PageGuide = {
    ...personnel,
    stops: [
        ...personnel.stops,
        menu(
            item("Details", "This page: the person's profile, membership and contact details."),
            item(
                "Files",
                "Documents kept for the person, such as their CV and training certificates."
            ),
            item(
                "HubSpot",
                "The person's HubSpot contact, and reconciling their email with HubSpot."
            ),
            item(
                "Institutions & Roles",
                "Every role the person holds on trials, sites, institutions and groups, which together decide their permissions."
            ),
            item("Permissions", "What the person can do, worked out from their current roles."),
            item("Notes", "Notes recorded about the person, such as correspondence and decisions."),
            item(
                "Login & Security",
                "The person's sign-in account: creating it, its state, and sending them a welcome email."
            ),
            item(
                "OpenClinica",
                "The OpenClinica roles created from the person's trial roles, and whether each has been set up."
            ),
            item(
                "Email Preferences",
                "Where the person's email goes for each trial, such as to a secondary address."
            )
        )
    ]
};

const personnelRole: PageGuide = {
    intro: "One of this person's roles: where it applies, what it is, and when it started and ended. The trial or institution chosen decides where the role's permissions apply.",
    stops: [
        field(
            "trialId",
            "Trial",
            "The trial the role is on, for trial and trial site roles. Leave empty for an institution role."
        ),
        field(
            "institutionId",
            "Institution",
            "The institution the role is at, for site and institution roles. Leave empty for a trial-wide role."
        ),
        field(
            "personnelId",
            "Person",
            "Who holds the role: this person. To give the role to someone else, end it and add a new role for them."
        ),
        field(
            "roleId",
            "Role",
            "The role held. Its permissions apply only within the trial or institution above, and it decides which emails the person receives there."
        ),
        field(
            "departmentId",
            "Department",
            "The department of the institution the person works in for this role, if any."
        ),
        field(
            "dateStarted",
            "Date Started",
            "When the role began; shown in the Period column and used in reports."
        ),
        field(
            "noEmailCommunication",
            "No Email Communication",
            "Tick to stop emails sent to this role from reaching the person, without ending the role."
        ),
        field(
            "active",
            "Active",
            "Clear to end the role. The person loses its permissions, and Date Ceased appears."
        ),
        field(
            "dateCeased",
            "Date Ceased",
            "When the role ended. It appears once Active is cleared and is required then."
        ),
        saveStop("Submit saves the role; ending it removes its permissions from the person.", Permission.PersonnelRoleUpdate)
    ],
    rules: [
        "Person, Role and Date Started are required; Date Ceased is required once the role is no longer active.",
        "A supporting site role needs a trial and an institution, and cannot also be a group role."
    ],
    notes: ["End roles rather than deleting them so the history of who held a role is kept."]
};

const groupRoles: RoleOwner = {
    kind: "group",
    owner: "this group",
    examples: "group chair, secretariat and group contacts",
    createPermission: Permission.GroupPersonnelRoleCreate,
    updatePermission: Permission.GroupPersonnelRoleUpdate
};

const groupNotes = notesGuides(
    "this group",
    Permission.GroupNotesCreate,
    Permission.GroupNotesUpdate
);
const personnelNotes = notesGuides(
    "this person",
    Permission.PersonnelNoteCreate,
    Permission.PersonnelNoteUpdate
);

/** The columns shared by the cross-scope role lists. */
const scopeColumn = column(
    "Scope",
    "Where the role applies: a trial, a site, a group, or everywhere (Global), with the department below when there is one."
);
const contactColumn = column(
    "Contact",
    "The email and phone number to use for the person in this role."
);
const periodColumn = column(
    "Period",
    "When the role started and, once it has ended, when it ended. Hover to see how long it has been held."
);

const groupsPersonnelGuides: Record<string, PageGuide> = {
    "/groups": {
        intro: "The cooperative groups trials are run through or with, such as TROG itself and international partner groups.",
        stops: [
            filterStop(
                "Type part of a group's name to filter. Inactive groups are hidden unless you switch them on."
            ),
            actionsStop(
                "Add creates a group. Search the list first so it is not added twice.",
                Permission.GroupCreate
            ),
            grid("Each row is one group; select it to open its details, trials and personnel.", [
                column("Group Name", "The group's usual name, as shown throughout the system."),
                column("Default", "Ticked for the group marked as the default group.")
            ])
        ]
    },
    "/groups/:groupId/details": {
        ...group,
        stops: [
            ...group.stops,
            menu(
                item(
                    "Details",
                    "This page: the group's identity, addresses and agreement details."
                ),
                item(
                    "Personnel",
                    "The people with roles in the group, such as the chair and secretariat."
                ),
                item("Trials", "The trials run through this group, opening each trial you can view."),
                item("Notes", "Notes recorded about the group, such as correspondence and decisions."),
                item(
                    "Configuration",
                    "Settings that apply only to this group, such as its item templates."
                )
            )
        ]
    },
    "/groups/create/details": createGuide(
        group,
        "Add a group. Enter its name, code and type, then its street address. The code cannot be changed once the group is saved.",
        [
            "Once saved, the group's personnel, notes and configuration are available from the side menu."
        ],
        { omit: ["active", "otherNames"] }
    ),
    "/groups/:groupId/notes": groupNotes.list,
    "/groups/:groupId/notes/:noteId": groupNotes.item,
    "/groups/:groupId/notes/create": groupNotes.create,
    "/groups/:groupId/personnel": roleListGuide(groupRoles),
    "/groups/:groupId/personnel/create": roleCreateGuide(groupRoles),
    "/groups/:groupId/personnel/:personnelRoleId": roleEditGuide(groupRoles),
    "/groups/:groupId/trials": {
        intro: "The trials run through this group. Trials you cannot view are listed but do not open.",
        stops: [
            filterStop("Type a trial number or title to filter the list."),
            grid("Each row is one trial; select it to open the trial, if you can view it.", [
                column("Trial Number", "The trial's display number, such as its TROG number."),
                column("Trial Title", "The trial's title, as recorded on its details page.")
            ])
        ]
    },
    "/groups/:groupId/configuration": {
        intro: "Settings that apply only to this group. Each card opens one area; you only see the areas you can manage.",
        stops: [
            cards(
                item(
                    "Trial Group Item Templates",
                    "The item templates available to this group's trials, which decide what the group collects for each trial."
                )
            )
        ]
    },

    "/personnel": {
        intro: "Everyone known to the system: investigators, site staff, TROG staff and other contacts. A person needs a record here before they can be given a role.",
        stops: [
            filterStop(
                "Type a name, username or email to filter. Inactive people are hidden unless you switch them on."
            ),
            actionsStop(
                "Group By Roles lists people by the roles they hold, across trials, sites and groups. Add creates a person; search first so the same person is not added twice.",
                Permission.PersonnelCreate
            ),
            grid("Each row is one person; select it to open their details and roles.", [
                column("Salutation", "How the person is addressed, such as Dr."),
                column("First Name", "The person's given name, as used in emails and lists."),
                column("Last Name", "The person's family name; sort by it to find people alphabetically."),
                column("User Name", "The name the person signs in with, if they have an account."),
                column("Email", "The person's primary email address, used for sign-in matching and emails.")
            ])
        ]
    },
    "/personnel/byroles": {
        intro: "Every role held by anyone, so you can find who holds a role across trials, sites, institutions and groups.",
        stops: [
            filterStop(
                "Type a role, person or trial to filter. Ended roles are hidden unless you switch on inactive records."
            ),
            stop(
                helpTourTargets.gridActions,
                "Back to the list",
                "List returns to the list of people, one row per person."
            ),
            grid("Each row is one role; select it to open the person who holds it.", [
                column("Role", "The role held; the same person can appear once per role."),
                column(
                    "Person",
                    "The person's full name, with their username below when they can sign in."
                ),
                scopeColumn,
                contactColumn,
                periodColumn
            ])
        ]
    },
    "/personnel/:personnelId/details": personnelDetails,
    "/personnel/create": createGuide(
        personnel,
        "Add a person. Enter their name first, then a set of contact details for their workplace with their primary email.",
        [
            "Once saved, give them roles from Institutions & Roles, and set up their sign-in from Login & Security if they need to sign in."
        ],
        { omit: ["active"] }
    ),
    "/personnel/:personnelId/notes": personnelNotes.list,
    "/personnel/:personnelId/notes/:noteId": personnelNotes.item,
    "/personnel/:personnelId/notes/create": personnelNotes.create,
    "/personnel/:personnelId/roles": {
        intro: "Every role this person holds, on trials, sites, institutions and groups. Their permissions are the combination of these roles.",
        stops: [
            filterStop(
                "Type to filter. Ended roles are hidden unless you switch on inactive records."
            ),
            actionsStop("Add gives the person a new role on a trial, site, institution or group.", Permission.PersonnelRoleCreate),
            grid("Select a row to change or end a role. Ended roles are greyed out.", [
                column("Group", "The group, for roles held in a group; empty for other roles."),
                column("Role", "The role held; the same person can appear once per role."),
                scopeColumn,
                contactColumn,
                periodColumn
            ])
        ],
        notes: ["Roles are ended rather than deleted, so the history of who held a role is kept."]
    },
    "/personnel/:personnelId/roles/:personnelRoleId": personnelRole,
    "/personnel/:personnelId/roles/create": createGuide(
        personnelRole,
        "Give this person a role. Choose the trial or institution it applies to, then the role and when it started.",
        ["Submit adds the role; the person gains its permissions straight away."],
        { omit: ["active", "dateCeased"] }
    ),
    "/personnel/:personnelId/activedirectory": {
        intro: "The person's sign-in account: whether it exists, whether it is enabled or locked, and its password state. The details are read-only and come from the directory.",
        stops: [
            stop(
                helpTourTargets.pageActions,
                "Account actions",
                "Synchronise Active Directory creates the account, or updates its name and email and re-enables it when eligible. Send welcome email opens a confirmation; Queue email then sends the person their username and a secure link to set their password.",
                Permission.PersonnelSecurityUpdate
            ),
            field(
                "displayName",
                "Display Name",
                "The name the directory shows for the account, from the person's first and last name."
            ),
            field(
                "userName",
                "Username",
                "The name the person signs in with, matching the Username on their details."
            ),
            field(
                "email",
                "Email",
                "The email held on the account, which should match the person's primary email."
            ),
            field(
                "distinguishedName",
                "Distinguished Name",
                "The account's full directory path, useful when raising a problem with IT support."
            ),
            field(
                "organisationalUnit",
                "Organisational Unit",
                "The directory folder the account is in. Synchronising moves it to the website users unit when appropriate."
            ),
            field(
                "enabled",
                "Enabled",
                "Whether the person can sign in. Synchronising re-enables an eligible disabled account."
            ),
            field(
                "accountLocked",
                "Account Locked",
                "Whether the account is locked, for example after too many failed sign-in attempts."
            ),
            field(
                "passwordChangeRequired",
                "Password Change Required",
                "Whether the person must choose a new password the next time they sign in."
            ),
            field(
                "passwordNeverExpires",
                "Password Never Expires",
                "Whether the password is exempt from expiry, as for service accounts."
            ),
            field(
                "passwordExpiry",
                "Password Expiry",
                "When the current password expires, after which the person must change it."
            ),
            field(
                "groups",
                "Groups",
                "The directory groups the account belongs to. Synchronising does not change them."
            )
        ],
        notes: [
            "No password is ever emailed; the welcome email contains a link for the person to set their own.",
            "When no account matches the person's username or primary email, the page says so and Synchronise creates one."
        ]
    },
    "/personnel/:personnelId/openclinica": {
        intro: "The person's OpenClinica user roles, created from their trial roles, and whether each has been set up in OpenClinica.",
        stops: [
            filterStop("Type a trial, institution or role to filter the list."),
            grid("Each row is one OpenClinica role for the person.", [
                column("Personnel", "The person the OpenClinica role belongs to."),
                column("Trial", "The trial whose role created the OpenClinica role."),
                column("Institution", "The site the role is at, for site-level roles."),
                column("OC Role", "The OpenClinica role their trial role maps to."),
                column("Study", "The OpenClinica study or study site the role is for."),
                column(
                    "Status",
                    "Whether the role has been set up in OpenClinica, is waiting, or failed."
                ),
                column("Error", "Why the last attempt to set up the role failed, if it did."),
                column(
                    "Actions",
                    "Synchronise sends the role to OpenClinica again; the email action queues an email confirming the person's OpenClinica role. The person needs an active account and a valid email."
                )
            ])
        ]
    },
    "/personnel/:personnelId/permissions": {
        intro: "What this person can do, worked out from their current roles. To change their permissions, change their roles.",
        stops: [
            filterStop("Type part of a permission to filter the list."),
            grid(
                "Permissions are grouped by where they apply (a trial, site, institution or group) and then by area.",
                [column("Permission", "What the permission allows the person to see or do.")]
            )
        ]
    },
    "/personnel/:personnelId/files": {
        intro: "Documents kept for the person, such as their CV, GCP certificate and other training records.",
        stops: [
            covering(
                field(
                    "sourceDocuments",
                    "Source Documents",
                    "Add a row per document: give it a name, choose its file type, then upload the file. Remove a row to remove the document."
                ),
                ["name", "Name"],
                ["fileType", "File Type"],
                ["fileInformation", "File"]
            ),
            saveStop(
                "Submit saves the documents; files are uploaded when the page is saved.",
                Permission.PersonnelFilesUpdate
            )
        ]
    },
    "/personnel/:personnelId/hubspot": {
        intro: "The person's contact in HubSpot, matched by their primary email. The details are read-only here and change in HubSpot.",
        stops: [
            stop(
                helpTourTargets.pageActions,
                "HubSpot actions",
                "Open in HubSpot opens the contact in HubSpot. Reconcile email makes the person's primary email the address used in Active Directory and HubSpot.",
                Permission.PersonnelHubspotUpdate
            ),
            stop(
                hubspotTourTargets.contact,
                "Contact details",
                "The fields HubSpot holds for the person, such as their membership, organisation and address."
            )
        ],
        notes: [
            "A warning shows when the emails do not match or the HubSpot identity needs review. If HubSpot cannot be updated during a reconcile, Personnel and Active Directory stay aligned and the reconcile can be retried."
        ]
    },
    "/personnel/:personnelId/emails": {
        intro: "Where this person's email goes, per trial. Preferences change where email goes, not whether they receive it; switching off email on one of their roles still wins.",
        stops: [
            stop(
                preferenceTourTargets.trial,
                "Trial",
                "Choose the trial to set preferences for; other trials are unaffected. Once chosen, its preferences appear below. Save or cancel your changes before switching trial.",
                Permission.PersonnelEmailPreferencesUpdate
            )
        ]
    }
};

export default groupsPersonnelGuides;
